import { identify, trackEvent } from "@centered/analytics"
import { AuthPage, AuthPagePage, getCookie, pages } from "@centered/auth"
import AuthPageWrapper from "@centered/components/dist/AuthPageWrapper"
import { useInvitedByUserId } from "@centered/components/dist/hooks/useInvitedByUserId"
import {
  auth,
  createUser,
  firebase,
  signOut,
  useCurrentUser,
  firestorePOSTAsCurrentUser,
} from "@centered/firebase"
import { checkIsMobile } from "@centered/helpers"
import Head from "next/head"
import { useRouter } from "next/router"
import React, { useCallback, useEffect, useState } from "react"
import Header from "../../components/shared/Header"
import { SigninHero } from "../../components/v3-landing-page/HeroSection"

// Extend the Window interface
declare global {
  interface Window {
    tolt_referral: string | null
    tolt: {
      signup: (email: string | null) => void
    }
  }
}

const LoginPage: React.FunctionComponent = () => {
  const router = useRouter()
  const [initialPage, setInitialPage] = useState<AuthPagePage | undefined>(
    undefined
  )
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loggingIn, setLoggingIn] = useState(false)
  const [usingGoogle, setUsingGoogle] = useState(false)
  const checkedForRedirect = true

  const { currentUser, loading } = useCurrentUser()
  const [authError, setAuthError] = useState<string | undefined>()
  const [invitedByUserId] = useInvitedByUserId()
  const isEmailOnly = router.query.emailOnly === "true"

  // After login, send custom token to chrome extension with id: dmgikpnmicdemepagiabhmoongkgdeil  
  useEffect(() => {
    async function sendCustomToken() {
      if (isAuthenticated && currentUser) {
        try {
          const {
            result: { customToken }
          } = await firestorePOSTAsCurrentUser<{ result: { customToken: string } }>("createCustomAuthToken", {});
    
          // @ts-expect-error
          if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
            const extensionId = "dmgikpnmicdemepagiabhmoongkgdeil"; //this id is the public-facing id, unique to the extension
            // @ts-expect-error
            chrome.runtime.sendMessage(
              extensionId,
              { type: "logged-in", customToken },
              () => {}
            );
          } else {
            console.warn("Chrome APIs are not available.");
          }
        } catch (error) {}
      }
    }
  
    sendCustomToken();
  }, [isAuthenticated, currentUser]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: any) => {
      if (!user || !usingGoogle) return
      console.debug("[authRoute:onAuthStateChanged] has user", user)
      setLoggingIn(true)
      const { uid, displayName, email, picture } = user
      const fbp = getCookie("_fbp")
      const fbc = getCookie("_fbc")
      const ttclid = getCookie("ttclid")
      const gclaw = getCookie("_gcl_aw")?.split(".")
      const gclid = gclaw ? gclaw[gclaw.length - 1] : undefined

      identify(uid, {
        id: uid,
        name: displayName,
        email: email,
      })

      console.debug("[authRoute:onAuthStateChanged] calling createUser")
      const isNewUser = await createUser(displayName, {
        avatarUrl: picture,
        invitedByUserId: invitedByUserId ?? undefined,
        conversionTracking: {
          fbc,
          fbp,
          gclid,
          ttclid,
        },
      })
      trackEvent(isNewUser ? "SignUp" : "Login", {
        id: uid,
        name: displayName,
        email: email,
        authMethod: firebase.auth.GoogleAuthProvider.PROVIDER_ID,
        referredBy: invitedByUserId ?? undefined,
        fbp,
        fbc,
        gclid,
        ttclid,
        isMobile: checkIsMobile(),
      })

      if (isNewUser && window.tolt_referral) {
        window.tolt.signup(email)
      }

      setIsAuthenticated(true)
    })
    return () => unsubscribe()
  }, [usingGoogle])

  const handleGoogleLogin = useCallback(async () => {
    setAuthError(undefined)
    setUsingGoogle(true)
    const provider = new firebase.auth.GoogleAuthProvider()
    provider.addScope("https://www.googleapis.com/auth/userinfo.email")
    provider.addScope("https://www.googleapis.com/auth/userinfo.profile")
    auth.signInWithPopup(provider).catch((e) => {
      console.error(e)
      setAuthError(e.message)
      setLoggingIn(false)
      setUsingGoogle(false)
    })
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      const page = router.query.authRoute as AuthPagePage

      if (
        router.query.redirect &&
        !router.query.redirect.includes("app") &&
        !router.query.redirect.includes("download")
      ) {
        router.push(router.query.redirect as string)
      } else {
        if (
          page !== "connect-desktop" &&
          page !== "connect-chrome-extension" &&
          page !== "account-connected" &&
          page !== "pick-password" &&
          page !== "chrome-extension-uninstall"
        ) {
          const isMobile = checkIsMobile()

          if (isMobile) {
            router.replace("/download?fromAuth=true")
          } else {
            router.push("/app")
          }
        } else {
          setInitialPage(page)
        }
      }
    }
  }, [isAuthenticated])

  const [checkedForAuthenticatedUser, setCheckedForAuthenticatedUser] =
    useState(false)

  useEffect(() => {
    const page = router.query.authRoute as AuthPagePage
    if (!page) return
    if (
      page === "login" ||
      (page === "signup" && !checkedForAuthenticatedUser)
    ) {
      signOut().then(() => {
        // It takes a bit for currentUser to become null
        // So this is hack to make sure we don't run into weird race conditions
        setTimeout(() => {
          setCheckedForAuthenticatedUser(true)
        }, 500)
      })
    } else {
      setCheckedForAuthenticatedUser(true)
    }
  }, [router.query.authRoute])

  useEffect(() => {
    if (loading === false && checkedForAuthenticatedUser)
      setIsAuthenticated(Boolean(currentUser?.id))
  }, [loading, checkedForAuthenticatedUser, currentUser?.id])

  useEffect(() => {
    if (loading || !checkedForRedirect) return
    const page = router.query.authRoute as AuthPagePage
    const idx = pages.indexOf(page)
    if (idx >= 0) {
      if (page === "connect-desktop" && !currentUser?.id) {
        router.replace("/auth/login?redirect=/auth/connect-desktop")
        return
      }

      if (page === "connect-chrome-extension" && !currentUser?.id) {
        router.replace(
          `/auth/login?redirect=${encodeURIComponent(
            `/auth/connect-chrome-extension?extensionId=${
              router.query.extensionId ?? ""
            }`
          )}`
        )
        return
      }
      setInitialPage(page)
    }
  }, [router.query.authRoute, currentUser?.id, loading, checkedForRedirect])

  if (initialPage === undefined) return null
  if (!checkedForRedirect) return null
  if (
    isAuthenticated &&
    initialPage !== "connect-desktop" &&
    initialPage !== "connect-chrome-extension" &&
    initialPage !== "account-connected" &&
    initialPage !== "pick-password" &&
    initialPage !== "chrome-extension-uninstall"
  )
    return null

  return (
    <div className='min-h-screen bg-v5-darker-gray'>
      <Header />

      <AuthPageWrapper
        onLogoClick={() => {
          router.push("/")
        }}
        hideLogo={true}
        sidebar={
          ["connect-desktop", "connect-chrome-extension"].includes(
            initialPage
          ) ||
          (initialPage === "login" && !isEmailOnly) ? undefined : (
            <SigninHero />
          )
        }
      >
        <Head>
          <title>Sukha - Authenticate</title>
        </Head>

        <AuthPage
          initialPage={initialPage}
          chromeExtensionUninstallUserId={router.query.userId as string}
          onPageChange={(page) => {
            if (page !== router.query.authRoute) {
              const url = `/auth/${page}${
                router.query.redirect
                  ? `?redirect=${encodeURIComponent(
                      router.query.redirect as string
                    )}`
                  : ""
              }`
              router.push(url, url, { shallow: true })
            }
          }}
          resetPasswordToken={router.query.oobCode as string}
          onGoogleLogin={() => {
            handleGoogleLogin()
          }}
          isEmailOnly={isEmailOnly}
          chromeExtensionId={router.query.extensionId as string}
          authError={authError}
          isLoggingIn={loggingIn}
        />
      </AuthPageWrapper>
    </div>
  )
}

export default LoginPage
